import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { useData, useDrivers } from "../store/dataStore";
import { useDrill } from "../store/drillStore";
import { filterRows } from "../query/runDrill";
import { attainment } from "../query/metrics";
import { fmtMoney, fmtPct, fmtNum } from "../query/format";
import { KpiCard } from "../components/KpiCard";
import { Drillable } from "../components/Drillable";
import { DriverCell } from "../components/DriverCell";

function computeProductivity(tables, quotaOverrides) {
  const bookings = tables.fact_bookings || [];
    const won = bookings.filter((b) => b.status === "won");
    const lost = bookings.filter((b) => b.status === "lost");
    const reps = tables.dim_rep || [];
    const repRows = reps.map((r) => {
      const deals = won.filter((b) => b.rep_id === r.rep_id);
      const quota = quotaOverrides[r.rep_id] ?? r.quota;
      const closedWon = deals.reduce((s, d) => s + d.amount, 0);
      const cycles = deals.map((d) => d.sales_cycle_days).filter(Boolean);
      return { ...r, quota, closedWon, deals: deals.length, att: attainment(closedWon, quota), cycle: cycles.length ? cycles.reduce((a, b) => a + b, 0) / cycles.length : 0 };
    }).sort((a, b) => b.att - a.att);
    const segRows = ["Enterprise", "Mid-Market", "Commercial"].map((segment) => {
      const segReps = repRows.filter((r) => r.segment === segment);
      const closedWon = segReps.reduce((s, r) => s + r.closedWon, 0);
      const quota = segReps.reduce((s, r) => s + r.quota, 0);
      return { segment, closedWon, quota, att: attainment(closedWon, quota) * 100 };
    });
    const teamWon = won.reduce((s, d) => s + d.amount, 0);
    const teamQuota = repRows.reduce((s, r) => s + r.quota, 0);
    const cycles = won.map((d) => d.sales_cycle_days).filter(Boolean);
    return { won, lost, repRows, segRows, teamWon, teamQuota, avgCycle: cycles.length ? cycles.reduce((a, b) => a + b, 0) / cycles.length : 0 };
}

export default function Productivity() {
  const tables = useData((s) => s.tables);
  const { quotaOverrides, setQuotaOverride } = useDrivers();
  const openAt = useDrill((s) => s.openAt);
  const { won, lost, repRows, segRows, teamWon, teamQuota, avgCycle } = useMemo(() => computeProductivity(tables, quotaOverrides), [tables, quotaOverrides]);

  const teamAtt = attainment(teamWon, teamQuota) * 100;
  const winRate = won.length + lost.length ? (won.length / (won.length + lost.length)) * 100 : 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-black tracking-tight" style={{ color: "var(--navy)" }}>Sales Productivity</h2>
        <p className="text-sm mt-1" style={{ color: "var(--slate)" }}>Attainment = closedWon / quota, rolled up from {won.length} closed-won deals. Quotas are editable drivers.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard testId="kpi-team-attainment" label="Team Attainment" value={fmtPct(teamAtt, 1)} sub={`${fmtMoney(teamWon)} of ${fmtMoney(teamQuota)} quota`} status={teamAtt >= 85 ? "pass" : teamAtt >= 70 ? "watch" : "miss"} progress={teamAtt / 100} module="productivity" seed={[]} />
        <KpiCard testId="kpi-closed-won" label="Closed Won" value={fmtMoney(teamWon)} sub={`${won.length} deals`} module="productivity" seed={[]} />
        <KpiCard testId="kpi-win-rate" label="Win Rate" value={fmtPct(winRate, 1)} sub={`${won.length}W / ${lost.length}L`} status={winRate >= 55 ? "pass" : "watch"} module="pipeline" seed={[{ field: "stage", value: "Closed Won" }]} />
        <KpiCard testId="kpi-avg-cycle" label="Avg Sales Cycle" value={`${fmtNum(avgCycle, 0)}d`} sub="Closed-won deals" module="productivity" seed={[]} />
      </div>

      <div className="panel p-5">
        <div className="text-[11px] uppercase tracking-widest font-bold mb-3" style={{ color: "var(--slate)" }}>Attainment by segment — click a bar to drill to regions → reps → deals</div>
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={segRows} margin={{ top: 10, right: 10, bottom: 0, left: 10 }}>
              <XAxis dataKey="segment" tick={{ fontSize: 12, fill: "var(--slate)" }} />
              <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: "var(--slate)" }} width={44} />
              <ReferenceLine y={100} stroke="var(--warn)" strokeDasharray="4 4" />
              <Tooltip content={({ active, payload, label }) => active && payload?.length ? (
                <div className="rounded-md shadow-lg px-3 py-2 text-xs font-mono" style={{ background: "var(--navy)", color: "#fff" }}>
                  <div className="font-bold" style={{ color: "var(--cyan)" }}>{label}</div>
                  <div style={{ color: "var(--teal)" }}>{fmtPct(payload[0].value, 1)} · {fmtMoney(payload[0].payload.closedWon)}</div>
                </div>) : null} />
              <Bar dataKey="att" fill="var(--teal)" radius={[3, 3, 0, 0]} cursor="pointer" onClick={(d) => d && openAt("productivity", [{ field: "segment", value: d.segment }])} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="panel overflow-x-auto">
        <table className="dtable" data-testid="rep-table">
          <thead><tr><th>Rep</th><th>Segment</th><th>Region</th><th className="text-right">Closed Won</th><th className="text-right">Quota (driver)</th><th className="text-right">Attainment</th><th className="text-right">Avg Cycle</th></tr></thead>
          <tbody>
            {repRows.map((r) => (
              <tr key={r.rep_id}>
                <td className="font-semibold" style={{ color: "var(--navy)" }}>{r.name}</td>
                <td style={{ color: "var(--slate)" }}>{r.segment}</td>
                <td style={{ color: "var(--slate)" }}>{r.region}</td>
                <td className="text-right">
                  <Drillable module="productivity" seed={[{ field: "segment", value: r.segment }, { field: "region", value: r.region }, { field: "rep_id", value: r.rep_id }]} testId={`drill-rep-${r.rep_id}`} className="inline-block">
                    <span className="fact-cell pr-4">{fmtMoney(r.closedWon)}</span>
                  </Drillable>
                </td>
                <td className="text-right">
                  <DriverCell testId={`driver-quota-${r.rep_id}`} value={r.quota} display={fmtMoney(r.quota)} onSave={(v) => setQuotaOverride(r.rep_id, v)} />
                </td>
                <td className="text-right num font-bold" style={{ color: r.att >= 1 ? "var(--good)" : r.att >= 0.7 ? "var(--warn)" : "var(--bad)" }}>{fmtPct(r.att * 100, 0)}</td>
                <td className="text-right num" style={{ color: "var(--slate)" }}>{r.cycle ? `${fmtNum(r.cycle, 0)}d` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
